import { FormEvent, useEffect, useMemo, useState } from "react";
import { api, ApiError, formatCnpj, formatDate } from "./api";
import type {
  Agent,
  Certificate,
  Company,
  Dashboard,
  Execution,
  Guide,
  Team,
  User,
} from "./types";
import logoLight from "./assets/brand/logo-light.png";
import symbol from "./assets/brand/symbol.png";
import CompaniesPageV2 from "./pages/CompaniesPage";
import SettingsPageV2 from "./pages/SettingsPage";
import RulesPage from "./pages/RulesPage";
import AssignmentsPage from "./pages/AssignmentsPage";

type Page =
  | "dashboard"
  | "companies"
  | "rules"
  | "assignments"
  | "certificates"
  | "executions"
  | "guides"
  | "users"
  | "settings";
type Flash = { kind: "ok" | "error"; text: string } | null;

const labels: Record<Page, string> = {
  dashboard: "Visão mensal",
  companies: "Empresas",
  rules: "Regras de busca",
  assignments: "Aplicar regras",
  certificates: "Certificados",
  executions: "Execuções",
  guides: "Guias",
  users: "Usuários e equipes",
  settings: "Configurações",
};
const navIcon: Record<Page, string> = {
  dashboard: "▦",
  companies: "▤",
  rules: "▦",
  assignments: "↳",
  certificates: "◇",
  executions: "↻",
  guides: "▱",
  users: "♙",
  settings: "⚙",
};
const statusLabel: Record<string, string> = {
  agendada: "Agendada",
  na_fila: "Na fila",
  executando: "Executando",
  aguardando_portal: "Aguardando portal",
  aguardando_intervencao: "Intervenção",
  concluida: "Concluída",
  falhou: "Falhou",
  cancelada: "Cancelada",
};
const navigation: { title: string; pages: Page[] }[] = [
  { title: "Acompanhar", pages: ["dashboard", "executions", "guides"] },
  { title: "Configurar", pages: ["companies", "rules", "assignments"] },
  { title: "Administração", pages: ["certificates", "users", "settings"] },
];

function Login({ onLogin }: { onLogin: (user: User) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      onLogin(
        (
          await api<{ user: User }>("/api/auth/login", {
            method: "POST",
            body: JSON.stringify({ email, password }),
          })
        ).user,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível entrar");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="login-shell">
      <section className="login-brand">
        <img className="login-logo" src={logoLight} alt="Biason" />
        <div className="login-rule" />
        <p>GESTÃO CONTÁBIL INTELIGENTE</p>
        <h1>Busca Guia INSS</h1>
        <span>Automação segura e rastreável das guias mensais.</span>
      </section>
      <form className="login-card" onSubmit={submit}>
        <img className="login-symbol" src={symbol} alt="" />
        <small>ACESSO AO PAINEL</small>
        <h2>Bem-vindo</h2>
        <p>Entre com sua conta corporativa.</p>
        <label>
          E-mail
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>
        <label>
          Senha
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && <div className="alert error">{error}</div>}
        <button className="primary" disabled={busy}>
          {busy ? "Entrando…" : "Entrar"}
        </button>
      </form>
    </main>
  );
}

function TokenPage({
  kind,
  onLogin,
}: {
  kind: "setup" | "accept-invite" | "reset-password";
  onLogin: (user: User) => void;
}) {
  const token = new URLSearchParams(location.search).get("token") ?? "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const setup = kind === "setup";
  const title = setup
    ? "Configuração inicial"
    : kind === "accept-invite"
      ? "Aceitar convite"
      : "Redefinir senha";
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (setup) {
        const r = await api<{ user: User }>("/api/auth/setup", {
          method: "POST",
          body: JSON.stringify({ token, name, email, password }),
        });
        onLogin(r.user);
      } else {
        await api(`/api/auth/${kind}`, {
          method: "POST",
          body: JSON.stringify({ token, password }),
        });
        location.href = "/";
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível concluir",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="login-shell">
      <section className="login-brand">
        <img className="login-logo" src={logoLight} alt="Biason" />
        <div className="login-rule" />
        <p>GESTÃO CONTÁBIL INTELIGENTE</p>
        <h1>Busca Guia INSS</h1>
        <span>Ambiente protegido para a operação mensal.</span>
      </section>
      <form className="login-card" onSubmit={submit}>
        <img className="login-symbol" src={symbol} alt="" />
        <small>PRIMEIRO ACESSO</small>
        <h2>{title}</h2>
        <p>
          {setup
            ? "Crie a conta administradora do sistema."
            : "Defina uma senha forte para continuar."}
        </p>
        {setup && (
          <>
            <label>
              Nome
              <input
                required
                minLength={2}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label>
              E-mail
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
          </>
        )}
        <label>
          Nova senha
          <input
            required
            type="password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {error && <div className="alert error">{error}</div>}
        <button className="primary" disabled={busy || !token}>
          {busy ? "Salvando…" : "Concluir"}
        </button>
        {!token && (
          <div className="alert error">O link não contém um token válido.</div>
        )}
      </form>
    </main>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="empty">
      <span>◎</span>
      <p>{text}</p>
    </div>
  );
}
function Status({ value }: { value: string }) {
  return (
    <span className={`status ${value}`}>{statusLabel[value] ?? value}</span>
  );
}
function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <section className="modal">
        <header>
          <h2>{title}</h2>
          <button className="icon-button" onClick={onClose}>
            ×
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function DashboardPage({
  data,
  executions,
  guides,
  companies,
  refresh,
}: {
  data: Dashboard | null;
  executions: Execution[];
  guides: Guide[];
  companies: Company[];
  refresh: () => void;
}) {
  if (!data) return <div className="loading">Carregando indicadores…</div>;
  const percent = data.companies_active
    ? Math.round((data.completed / data.companies_active) * 100)
    : 0;
  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">COMPETÊNCIA {data.current_competence}</p>
          <h1>Visão mensal</h1>
          <p>Acompanhe a busca e o processamento das guias do fluxo atual.</p>
        </div>
        <button className="secondary" onClick={refresh}>
          Atualizar
        </button>
      </div>
      <section className="metrics">
        <article>
          <span>Empresas ativas</span>
          <strong>{data.companies_active}</strong>
          <small>configuradas para busca</small>
        </article>
        <article>
          <span>Guias encontradas</span>
          <strong className="green">{data.completed}</strong>
          <small>{percent}% da competência</small>
        </article>
        <article>
          <span>Pendentes</span>
          <strong className="amber">{data.pending}</strong>
          <small>aguardando disponibilidade</small>
        </article>
        <article>
          <span>Exceções</span>
          <strong className="red">
            {data.failed + data.waiting_intervention}
          </strong>
          <small>falhas ou intervenção</small>
        </article>
      </section>
      <section className="dashboard-grid">
        <article className="card progress-card">
          <div className="card-title">
            <div>
              <h2>Progresso da competência</h2>
              <p>
                {data.completed} de {data.companies_active} empresas concluídas
              </p>
            </div>
            <strong>{percent}%</strong>
          </div>
          <div className="progress">
            <i style={{ width: `${percent}%` }} />
          </div>
          <div className="legend">
            <span>
              <i className="dot green-bg" />
              Concluídas {data.completed}
            </span>
            <span>
              <i className="dot amber-bg" />
              Pendentes {data.pending}
            </span>
            <span>
              <i className="dot red-bg" />
              Exceções {data.failed + data.waiting_intervention}
            </span>
          </div>
        </article>
        <article className="card agent-card">
          <div
            className={`agent-orb ${data.agent_online ? "online" : "offline"}`}
          >
            ●
          </div>
          <div>
            <h2>Agente Windows</h2>
            <p className={data.agent_online ? "green" : "red"}>
              {data.agent_online ? "Online e disponível" : "Offline"}
            </p>
            <small>Último sinal: {formatDate(data.last_heartbeat_at)}</small>
          </div>
        </article>
      </section>
      <section className="split">
        <article className="card">
          <div className="card-title">
            <h2>Execuções recentes</h2>
          </div>
          {executions.length ? (
            <table>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Competência</th>
                  <th>Status</th>
                  <th>Tentativa</th>
                  <th>Início</th>
                </tr>
              </thead>
              <tbody>
                {executions.slice(0, 6).map((x) => {
                  const company = companies.find((c) => c.id === x.company_id);
                  return (
                    <tr key={x.id}>
                      <td>
                        <strong>
                          {company?.trade_name ||
                            company?.legal_name ||
                            `Empresa #${x.company_id}`}
                        </strong>
                      </td>
                      <td>{x.competence}</td>
                      <td>
                        <Status value={x.status} />
                      </td>
                      <td>{x.attempt}</td>
                      <td>{formatDate(x.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <Empty text="Nenhuma execução registrada" />
          )}
        </article>
        <article className="card">
          <div className="card-title">
            <h2>Guias mais recentes</h2>
          </div>
          {guides.length ? (
            <div className="activity">
              {guides.slice(0, 5).map((g) => (
                <div key={g.id}>
                  <span className="file-icon">PDF</span>
                  <div>
                    <strong>{g.company_name}</strong>
                    <small>
                      {g.competence} · versão {g.version}
                    </small>
                  </div>
                  <b>{g.total_amount ? `R$ ${g.total_amount}` : "—"}</b>
                </div>
              ))}
            </div>
          ) : (
            <Empty text="As guias encontradas aparecerão aqui" />
          )}
        </article>
      </section>
    </>
  );
}

function CompaniesPage({
  items,
  teams,
  certificates,
  reload,
  flash,
}: {
  items: Company[];
  teams: Team[];
  certificates: Certificate[];
  reload: () => void;
  flash: (f: Flash) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    legal_name: "",
    trade_name: "",
    cnpj: "",
    team_id: teams[0]?.id ?? 0,
    certificate_id: null as number | null,
    search_start_day: 15,
    search_time: "08:00",
    active: true,
    notes: "",
    responsible_user_id: null,
  });
  const shown = items.filter((x) =>
    `${x.legal_name} ${x.trade_name} ${x.cnpj}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  async function save(e: FormEvent) {
    e.preventDefault();
    try {
      await api("/api/companies", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setOpen(false);
      flash({ kind: "ok", text: "Empresa cadastrada." });
      reload();
    } catch (err) {
      flash({
        kind: "error",
        text: err instanceof Error ? err.message : "Falha ao cadastrar",
      });
    }
  }
  async function importFile(file?: File) {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const r = await api<{ created: number; updated: number }>(
        "/api/companies/import",
        { method: "POST", body },
      );
      flash({
        kind: "ok",
        text: `Importação concluída: ${r.created} criadas e ${r.updated} atualizadas.`,
      });
      reload();
    } catch (err) {
      flash({
        kind: "error",
        text: err instanceof Error ? err.message : "Falha na importação",
      });
    }
  }
  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">CADASTROS</p>
          <h1>Empresas</h1>
          <p>Defina certificado, equipe e agenda de busca de cada CNPJ.</p>
        </div>
        <div className="actions">
          <label className="secondary upload">
            Importar planilha
            <input
              type="file"
              accept=".xlsx,.csv"
              onChange={(e) => importFile(e.target.files?.[0])}
            />
          </label>
          <button className="primary" onClick={() => setOpen(true)}>
            + Nova empresa
          </button>
        </div>
      </div>
      <div className="toolbar">
        <input
          className="search"
          placeholder="Buscar empresa ou CNPJ"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <a className="text-link" href="/api/companies/import-template">
          Baixar modelo
        </a>
      </div>
      <section className="card table-card">
        {shown.length ? (
          <table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>CNPJ</th>
                <th>Equipe</th>
                <th>Certificado</th>
                <th>Busca</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.trade_name || c.legal_name}</strong>
                    <small>{c.trade_name && c.legal_name}</small>
                  </td>
                  <td>{formatCnpj(c.cnpj)}</td>
                  <td>{teams.find((t) => t.id === c.team_id)?.name ?? "—"}</td>
                  <td>
                    {certificates.find((x) => x.id === c.certificate_id)
                      ?.name ?? <span className="red">Não definido</span>}
                  </td>
                  <td>
                    dia {c.search_start_day}, {c.search_time}
                  </td>
                  <td>
                    <span
                      className={`pill ${c.active ? "active" : "inactive"}`}
                    >
                      {c.active ? "Ativa" : "Inativa"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty text="Nenhuma empresa encontrada" />
        )}
      </section>
      {open && (
        <Modal title="Cadastrar empresa" onClose={() => setOpen(false)}>
          <form className="form-grid" onSubmit={save}>
            <label className="wide">
              Razão social
              <input
                required
                onChange={(e) =>
                  setForm({ ...form, legal_name: e.target.value })
                }
              />
            </label>
            <label>
              Nome fantasia
              <input
                onChange={(e) =>
                  setForm({ ...form, trade_name: e.target.value })
                }
              />
            </label>
            <label>
              CNPJ
              <input
                required
                placeholder="00.000.000/0000-00"
                onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
              />
            </label>
            <label>
              Equipe
              <select
                value={form.team_id}
                onChange={(e) =>
                  setForm({ ...form, team_id: Number(e.target.value) })
                }
              >
                {teams.map((t) => (
                  <option value={t.id} key={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Certificado
              <select
                value={form.certificate_id ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    certificate_id: e.target.value
                      ? Number(e.target.value)
                      : null,
                  })
                }
              >
                <option value="">Selecione</option>
                {certificates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Dia inicial
              <input
                type="number"
                min="1"
                max="28"
                value={form.search_start_day}
                onChange={(e) =>
                  setForm({ ...form, search_start_day: Number(e.target.value) })
                }
              />
            </label>
            <label>
              Horário
              <input
                type="time"
                value={form.search_time}
                onChange={(e) =>
                  setForm({ ...form, search_time: e.target.value })
                }
              />
            </label>
            <label className="wide">
              Observações
              <textarea
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </label>
            <footer className="wide">
              <button
                type="button"
                className="secondary"
                onClick={() => setOpen(false)}
              >
                Cancelar
              </button>
              <button className="primary">Salvar empresa</button>
            </footer>
          </form>
        </Modal>
      )}
    </>
  );
}

function CertificatesPage({
  items,
  agents,
  reload,
  flash,
}: {
  items: Certificate[];
  agents: Agent[];
  reload: () => void;
  flash: (f: Flash) => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    subject: "",
    issuer: "",
    serial: "",
    agent_id: null as number | null,
    active: true,
  });
  async function save(e: FormEvent) {
    e.preventDefault();
    try {
      await api("/api/certificates", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setOpen(false);
      flash({ kind: "ok", text: "Certificado cadastrado." });
      reload();
    } catch (err) {
      flash({
        kind: "error",
        text: err instanceof Error ? err.message : "Falha",
      });
    }
  }
  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">IDENTIDADES DIGITAIS</p>
          <h1>Certificados</h1>
          <p>
            Cadastre somente a identidade pública dos certificados instalados no
            Windows.
          </p>
        </div>
        <button className="primary" onClick={() => setOpen(true)}>
          + Novo certificado
        </button>
      </div>
      <div className="notice">
        O arquivo e a chave privada nunca são enviados ao painel. A seleção usa
        titular, emissor e número de série exatos.
      </div>
      <section className="card table-card">
        {items.length ? (
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Titular (subject)</th>
                <th>Série</th>
                <th>Agente</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                  </td>
                  <td className="truncate">{c.subject}</td>
                  <td>
                    <code>{c.serial}</code>
                  </td>
                  <td>
                    {agents.find((a) => a.id === c.agent_id)?.name ??
                      "Qualquer agente"}
                  </td>
                  <td>
                    <span
                      className={`pill ${c.active ? "active" : "inactive"}`}
                    >
                      {c.active ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty text="Nenhum certificado cadastrado" />
        )}
      </section>
      {open && (
        <Modal title="Cadastrar certificado" onClose={() => setOpen(false)}>
          <form className="form-grid" onSubmit={save}>
            <label className="wide">
              Nome amigável
              <input
                required
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="wide">
              Titular / Subject
              <input
                required
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
              />
            </label>
            <label className="wide">
              Emissor / Issuer
              <input
                required
                onChange={(e) => setForm({ ...form, issuer: e.target.value })}
              />
            </label>
            <label>
              Número de série
              <input
                required
                onChange={(e) => setForm({ ...form, serial: e.target.value })}
              />
            </label>
            <label>
              Agente
              <select
                onChange={(e) =>
                  setForm({
                    ...form,
                    agent_id: e.target.value ? Number(e.target.value) : null,
                  })
                }
              >
                <option value="">Qualquer agente</option>
                {agents.map((a) => (
                  <option value={a.id} key={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            <footer className="wide">
              <button
                type="button"
                className="secondary"
                onClick={() => setOpen(false)}
              >
                Cancelar
              </button>
              <button className="primary">Salvar</button>
            </footer>
          </form>
        </Modal>
      )}
    </>
  );
}

function ExecutionsPage({
  items,
  companies,
  reload,
  flash,
  canDelete,
}: {
  items: Execution[];
  companies: Company[];
  reload: () => void;
  flash: (f: Flash) => void;
  canDelete: boolean;
}) {
  const [selected, setSelected] = useState<number[]>([]);
  const [competence, setCompetence] = useState("");
  const [documentType, setDocumentType] = useState<"inss" | "fgts">("inss");
  const [filter, setFilter] = useState("");
  const shown = items.filter((item) => {
    const company = companies.find((value) => value.id === item.company_id);
    return `${item.id} ${item.competence} ${item.status} ${item.document_type} ${company?.trade_name || ""} ${company?.legal_name || ""}`
      .toLowerCase().includes(filter.toLowerCase());
  });
  async function removeExecution(item: Execution) {
    const company = companies.find((value) => value.id === item.company_id);
    if (!window.confirm(`Apagar execução #${item.id} de ${company?.trade_name || company?.legal_name || item.company_id}, competência ${item.competence}?`)) return;
    try {
      await api(`/api/executions/${item.id}`, { method: "DELETE" });
      flash({ kind: "ok", text: `Execução #${item.id} apagada.` });
      reload();
    } catch (error) { flash({ kind: "error", text: error instanceof Error ? error.message : "Falha ao apagar execução" }); }
  }
  async function run() {
    if (!selected.length)
      return flash({ kind: "error", text: "Selecione ao menos uma empresa." });
    if (!competence)
      return flash({
        kind: "error",
        text: "Selecione a competência que deseja buscar.",
      });
    const [year, month] = competence.split("-");
    try {
      await api("/api/executions", {
        method: "POST",
        body: JSON.stringify({
          company_ids: selected,
          competence: `${month}/${year}`,
          force: true,
          document_type: documentType,
        }),
      });
      flash({
        kind: "ok",
        text: `Busca ${documentType === "fgts" ? "FGTS Digital" : "INSS"} de ${month}/${year} adicionada à fila.`,
      });
      setSelected([]);
      reload();
    } catch (e) {
      flash({ kind: "error", text: e instanceof Error ? e.message : "Falha" });
    }
  }
  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">OPERAÇÃO</p>
          <h1>Execuções</h1>
          <p>Fila sequencial, tentativas e andamento em tempo real.</p>
        </div>
        <button className="secondary" onClick={reload}>
          Atualizar
        </button>
      </div>
      <section className="card manual-run forced-run">
        <div>
          <h2>Busca manual do fluxo atual</h2>
          <p>
            Emite a guia inteira da competência escolhida. A emissão por grupo de códigos ainda está em preparação.
          </p>
        </div>
        <label className="manual-field">
          <span>Documento</span>
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value as "inss" | "fgts")}
          >
            <option value="inss">INSS — DARF</option>
            <option value="fgts">FGTS Digital — GFD</option>
          </select>
        </label>
        <label className="manual-field">
          <span>Empresa</span>
          <select
            value=""
            onChange={(e) => {
              const id = Number(e.target.value);
              if (id && !selected.includes(id)) setSelected([...selected, id]);
            }}
          >
            <option value="">Adicionar empresa…</option>
            {companies
              .filter((c) => c.active && (documentType !== "fgts" || c.fgts_enabled))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.trade_name || c.legal_name}
                </option>
              ))}
          </select>
        </label>
        <label className="manual-field">
          <span>Competência</span>
          <input
            type="month"
            value={competence}
            onChange={(e) => setCompetence(e.target.value)}
          />
        </label>
        <button className="primary" onClick={run}>
          Forçar busca
        </button>
        {selected.length > 0 && (
          <div className="wide tags">
            {selected.map((id) => (
              <button
                key={id}
                onClick={() => setSelected(selected.filter((x) => x !== id))}
              >
                {companies.find((c) => c.id === id)?.trade_name ||
                  companies.find((c) => c.id === id)?.legal_name}{" "}
                ×
              </button>
            ))}
          </div>
        )}
      </section>
      <div className="toolbar"><input className="search" placeholder="Buscar empresa, competência ou status" value={filter} onChange={(event) => setFilter(event.target.value)} /></div>
      <section className="card table-card">
        {shown.length ? (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Documento</th>
                <th>Empresa</th>
                <th>Competência</th>
                <th>Gatilho</th>
                <th>Status</th>
                <th>Tentativa</th>
                <th>Atualização</th>
                {canDelete && <th />}
              </tr>
            </thead>
            <tbody>
              {shown.map((x) => (
                <tr key={x.id}>
                  <td>
                    <code>#{x.id}</code>
                  </td>
                  <td>{x.document_type === "fgts" ? "FGTS Digital" : "INSS"}</td>
                  <td>
                    {companies.find((c) => c.id === x.company_id)?.trade_name ||
                      companies.find((c) => c.id === x.company_id)
                        ?.legal_name ||
                      x.company_id}
                  </td>
                  <td>{x.competence}</td>
                  <td>
                    {x.trigger === "manual_forcada"
                      ? "Busca forçada"
                      : x.trigger}
                  </td>
                  <td>
                    <Status value={x.status} />
                    {x.error_message && (
                      <small className="error-text">{x.error_message}</small>
                    )}
                  </td>
                  <td>{x.attempt}</td>
                  <td>{formatDate(x.created_at)}</td>
                  {canDelete && <td><button className="table-action danger-action" disabled={x.status === "executando"} onClick={() => removeExecution(x)}>Apagar</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty text="Nenhuma execução registrada" />
        )}
      </section>
    </>
  );
}

function money(value: string | null | undefined) {
  return value == null
    ? "—"
    : Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
}
function GuidesPage({ items, reload, flash, canDelete }: { items: Guide[]; reload: () => void; flash: (f: Flash) => void; canDelete: boolean }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Guide | null>(null);
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState("");
  const shown = items.filter((g) =>
    `${g.company_name} ${g.cnpj} ${g.competence}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  async function openGuide(guide: Guide) {
    setLoading(true);
    setReadError("");
    try {
      setSelected((await api<{ item: Guide }>(`/api/guides/${guide.id}`)).item);
    } finally {
      setLoading(false);
    }
  }
  async function reprocess() {
    if (!selected) return;
    setReading(true);
    setReadError("");
    try {
      setSelected(
        (
          await api<{ item: Guide }>(`/api/guides/${selected.id}/reprocess`, {
            method: "POST",
          })
        ).item,
      );
    } catch (error) {
      setReadError(
        error instanceof Error
          ? error.message
          : "Não foi possível reler o PDF.",
      );
    } finally {
      setReading(false);
    }
  }
  async function removeGuide(guide: Guide) {
    if (!window.confirm(`Apagar a guia ${guide.document_number || `#${guide.id}`} de ${guide.company_name}, competência ${guide.competence}, e seu PDF?`)) return;
    try {
      const result = await api<{ deleted: number; file_removed: boolean }>(`/api/guides/${guide.id}`, { method: "DELETE" });
      setSelected(null);
      flash({ kind: result.file_removed ? "ok" : "error", text: result.file_removed ? "Guia e PDF apagados." : "Guia apagada; o PDF não pôde ser removido do armazenamento." });
      reload();
    } catch (error) { flash({ kind: "error", text: error instanceof Error ? error.message : "Falha ao apagar guia" }); }
  }
  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">DOCUMENTOS</p>
          <h1>Guias</h1>
          <p>
            Versões, dados extraídos e PDFs disponíveis conforme a retenção
            configurada.
          </p>
        </div>
      </div>
      <div className="toolbar">
        <input
          className="search"
          placeholder="Buscar empresa, CNPJ ou competência"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <section className="card table-card">
        {shown.length ? (
          <table>
            <thead>
              <tr>
                <th>Documento</th>
                <th>Empresa</th>
                <th>Competência</th>
                <th>Versão</th>
                <th>Total</th>
                <th>Hash SHA-256</th>
                <th>Coleta</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((g) => (
                <tr
                  key={g.id}
                  className="clickable-row"
                  onClick={() => openGuide(g)}
                >
                  <td>{g.document_type === "fgts" ? "FGTS Digital" : "INSS"}</td>
                  <td>
                    <strong>{g.company_name}</strong>
                    <small>{formatCnpj(g.cnpj)}</small>
                  </td>
                  <td>{g.competence}</td>
                  <td>
                    v{g.version}{" "}
                    {g.current && <span className="pill active">Atual</span>}
                  </td>
                  <td>
                    <strong>{money(g.total_amount)}</strong>
                  </td>
                  <td>
                    <code title={g.sha256}>{g.sha256.slice(0, 12)}…</code>
                  </td>
                  <td>{formatDate(g.created_at)}</td>
                  <td>
                    {g.file_available ? (
                      <a
                        className="download"
                        href={`/api/guides/${g.id}/download`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Baixar
                      </a>
                    ) : (
                      <span className="muted">Expirado</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty text="Nenhuma guia encontrada" />
        )}
      </section>
      {loading && <div className="detail-loading">Carregando detalhes…</div>}
      {selected && (
        <Modal
          title="Documento de arrecadação"
          onClose={() => setSelected(null)}
        >
          <div className="guide-summary">
            <div>
              <span>Empresa</span>
              <strong>{selected.company_name}</strong>
              <small>{formatCnpj(selected.cnpj)}</small>
            </div>
            <div>
              <span>Competência</span>
              <strong>{selected.competence}</strong>
            </div>
            <div>
              <span>Documento</span>
              <strong>{selected.document_type === "fgts" ? `FGTS Digital${selected.guide_category ? ` · ${selected.guide_category}` : ""}` : "INSS · DARF"}</strong>
            </div>
            <div>
              <span>Número do documento</span>
              <strong>{selected.document_number || "—"}</strong>
            </div>
            <div>
              <span>Vencimento</span>
              <strong>{selected.due_date || "—"}</strong>
            </div>
            <div>
              <span>Valor total</span>
              <strong>{money(selected.total_amount)}</strong>
            </div>
            <div>
              <span>Extração</span>
              <strong>
                {selected.extraction_status === "extraido"
                  ? "Completa"
                  : "Parcial"}
              </strong>
            </div>
          </div>
          <h3 className="composition-title">
            Composição do documento de arrecadação
          </h3>
          {selected.document_type === "fgts" && selected.fgts_items?.length ? (
            <div className="composition-table">
              <table>
                <thead><tr><th>Competência</th><th>Componente</th><th>Trabalhadores</th><th>Valor</th></tr></thead>
                <tbody>{selected.fgts_items.map((item) => (
                  <tr key={item.id}><td>{item.competence}</td><td>{item.description}</td><td>{item.workers ?? "—"}</td><td><strong>{money(item.amount)}</strong></td></tr>
                ))}</tbody>
                <tfoot><tr><td colSpan={3}><strong>Total da composição</strong></td><td><strong>{money(selected.fgts_items.reduce((sum,item)=>sum+Number(item.amount),0).toFixed(2))}</strong></td></tr></tfoot>
              </table>
            </div>
          ) : selected.items?.length ? (
            <div className="composition-table">
              <table>
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Denominação</th>
                    <th>Principal</th>
                    <th>Multa</th>
                    <th>Juros</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.revenue_code}</strong>
                      </td>
                      <td>{item.description}</td>
                      <td>{money(item.principal)}</td>
                      <td>{money(item.fine)}</td>
                      <td>{money(item.interest)}</td>
                      <td>
                        <strong>{money(item.total)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>
                      <strong>Totais</strong>
                    </td>
                    <td>
                      {money(
                        selected.items
                          .reduce(
                            (sum, item) => sum + Number(item.principal),
                            0,
                          )
                          .toFixed(2),
                      )}
                    </td>
                    <td>
                      {money(
                        selected.items
                          .reduce((sum, item) => sum + Number(item.fine), 0)
                          .toFixed(2),
                      )}
                    </td>
                    <td>
                      {money(
                        selected.items
                          .reduce((sum, item) => sum + Number(item.interest), 0)
                          .toFixed(2),
                      )}
                    </td>
                    <td>
                      <strong>
                        {money(
                          selected.items
                            .reduce((sum, item) => sum + Number(item.total), 0)
                            .toFixed(2),
                        )}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <Empty text="A composição não foi identificada neste PDF." />
          )}
          {readError && (
            <div className="alert error reprocess-error">{readError}</div>
          )}
          <footer className="guide-actions">
            <small>Parser {selected.parser_version || "—"}</small>
            <div className="actions">
              {selected.file_available && (
                <button
                  className="secondary"
                  disabled={reading}
                  onClick={reprocess}
                >
                  {reading ? "Lendo PDF…" : "Tentar leitura novamente"}
                </button>
              )}
              {selected.file_available && (
                <a
                  className="primary button-link"
                  href={`/api/guides/${selected.id}/download`}
                >
                  Baixar PDF
                </a>
              )}
              {canDelete && <button className="secondary danger-action" onClick={() => removeGuide(selected)}>Apagar guia</button>}
            </div>
          </footer>
        </Modal>
      )}
    </>
  );
}

function UsersPage({
  user,
  teams,
  agents,
  reload,
  flash,
}: {
  user: User;
  teams: Team[];
  agents: Agent[];
  reload: () => void;
  flash: (f: Flash) => void;
}) {
  type UserForm = { name: string; email: string; role: string; team_id: number | null; coordinator_id: number | null; active: boolean };
  const emptyForm = (): UserForm => ({ name: "", email: "", role: "colaborador", team_id: teams[0]?.id ?? null, coordinator_id: null, active: true });
  const [users, setUsers] = useState<User[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [agentEditor, setAgentEditor] = useState<Agent | "new" | null>(null);
  const [agentForm, setAgentForm] = useState({ name: "" });
  const [agentKey, setAgentKey] = useState<string | null>(null);
  const [form, setForm] = useState<UserForm>(emptyForm);
  const [pendingInvite, setPendingInvite] = useState<{ url: string; warning: string } | null>(null);
  async function loadUsers() {
    const result = await api<{ items: User[] }>("/api/admin/users");
    setUsers(result.items);
  }
  useEffect(() => {
    if (user.role === "admin")
      loadUsers().catch(() => {});
  }, [user.role]);
  function inviteUser() {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  }
  function editUser(selected: User) {
    setEditing(selected);
    setForm({ name: selected.name, email: selected.email, role: selected.role,
      team_id: selected.team_id, coordinator_id: selected.coordinator_id, active: selected.active });
    setOpen(true);
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    try {
      const invite = editing ? null : await api<{ email_sent: boolean; invite_url?: string; warning?: string }>("/api/admin/users", {
        method: "POST", body: JSON.stringify(form),
      });
      if (editing) await api(`/api/admin/users/${editing.id}`, { method: "PUT", body: JSON.stringify(form) });
      setOpen(false);
      await loadUsers();
      if (invite?.invite_url) setPendingInvite({ url: invite.invite_url, warning: invite.warning ?? "O e-mail não foi enviado." });
      flash({
        kind: invite?.invite_url ? "error" : "ok",
        text: editing ? "Usuário atualizado." : invite?.invite_url
          ? "Usuário criado, mas o e-mail não foi enviado." : "Convite enviado por e-mail.",
      });
      setEditing(null);
    } catch (err) {
      flash({
        kind: "error",
        text: err instanceof Error ? err.message : "Falha",
      });
    }
  }
  async function resendInvite(selected: User) {
    try {
      const result = await api<{ invite_url?: string; warning?: string }>(`/api/admin/users/${selected.id}/resend-invite`, { method: "POST" });
      if (result.invite_url) {
        setPendingInvite({ url: result.invite_url, warning: result.warning ?? "O e-mail não foi enviado." });
        flash({ kind: "error", text: "O convite foi gerado, mas o e-mail não foi enviado." });
      } else {
        flash({ kind: "ok", text: `Convite reenviado para ${selected.email}.` });
      }
    } catch (err) {
      flash({ kind: "error", text: err instanceof Error ? err.message : "Falha ao reenviar convite." });
    }
  }
  function editAgent(agent: Agent | "new") {
    setAgentForm({ name: agent === "new" ? "" : agent.name });
    setAgentEditor(agent);
  }
  async function saveAgent(e: FormEvent) {
    e.preventDefault();
    try {
      if (agentEditor === "new") {
        const r = await api<{ api_key: string }>("/api/admin/agents", {
          method: "POST",
          body: JSON.stringify({ ...agentForm, version: "1.0.0" }),
        });
        setAgentKey(r.api_key);
        flash({ kind: "ok", text: "Agente registrado e pausado. Guarde a chave exibida." });
      } else if (agentEditor) {
        await api(`/api/admin/agents/${agentEditor.id}`, {
          method: "PUT",
          body: JSON.stringify(agentForm),
        });
        flash({ kind: "ok", text: "Agente atualizado." });
      }
      setAgentEditor(null);
      reload();
    } catch (e) {
      flash({ kind: "error", text: e instanceof Error ? e.message : "Falha" });
    }
  }
  async function pauseAgent(agent: Agent) {
    try {
      await api(`/api/admin/agents/${agent.id}/pause`, {
        method: "PATCH",
        body: JSON.stringify({ paused: !agent.paused }),
      });
      reload();
    } catch (e) {
      flash({ kind: "error", text: e instanceof Error ? e.message : "Falha" });
    }
  }
  async function deleteAgent(agent: Agent) {
    if (!window.confirm(`Excluir o agente ${agent.name}? Sua chave perderá acesso. Certificados vinculados devem ser reassociados antes.`)) return;
    try {
      await api(`/api/admin/agents/${agent.id}`, { method: "DELETE" });
      flash({ kind: "ok", text: "Agente excluído. O histórico foi preservado." });
      reload();
    } catch (e) {
      flash({ kind: "error", text: e instanceof Error ? e.message : "Falha" });
    }
  }
  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">ADMINISTRAÇÃO</p>
          <h1>Usuários e equipes</h1>
          <p>Acessos, responsabilidades e agentes de automação.</p>
        </div>
        {user.role === "admin" && (
          <button className="primary" onClick={inviteUser}>
            + Convidar usuário
          </button>
        )}
      </div>
      <section className="split">
        <article className="card">
          <div className="card-title">
            <h2>Usuários</h2>
          </div>
          {users.length ? (
            <div className="activity">
              {users.map((u) => (
                <div key={u.id}>
                  <span className="avatar">{u.name.charAt(0)}</span>
                  <div>
                    <strong>{u.name}</strong>
                    <small>{u.email} · {teams.find((t) => t.id === u.team_id)?.name ?? "Sem equipe"}</small>
                  </div>
                  <span className={`pill ${u.active ? "active" : "inactive"}`}>{u.active ? u.role : "Inativo"}</span>
                  {u.active && u.must_change_password && <button className="secondary user-edit" onClick={() => resendInvite(u)}>Reenviar convite</button>}
                  <button className="secondary user-edit" onClick={() => editUser(u)}>Editar</button>
                </div>
              ))}
            </div>
          ) : (
            <Empty
              text={
                user.role === "admin"
                  ? "Nenhum usuário adicional"
                  : "Apenas administradores visualizam usuários"
              }
            />
          )}
        </article>
        <article className="card">
          <div className="card-title">
            <h2>Equipes</h2>
          </div>
          <div className="activity">
            {teams.map((t) => (
              <div key={t.id}>
                <span className="avatar team">{t.name.charAt(0)}</span>
                <div>
                  <strong>{t.name}</strong>
                  <small>{t.active ? "Ativa" : "Inativa"}</small>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>
      {user.role === "admin" && (
        <section className="card agents">
          <div className="card-title">
            <div>
              <h2>Agentes Windows</h2>
              <p>Um agente executa uma empresa por vez.</p>
            </div>
            <button className="secondary" onClick={() => editAgent("new")}>
              Registrar agente
            </button>
          </div>
          {agents.length ? (
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Máquina</th>
                  <th>Versão</th>
                  <th>Último sinal</th>
                  <th>Estado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a.id}>
                    <td>{a.name}</td>
                    <td>{a.machine_name || "—"}</td>
                    <td>{a.version || "—"}</td>
                    <td>{formatDate(a.last_heartbeat_at)}</td>
                    <td>
                      <span
                        className={`pill ${a.paused ? "inactive" : "active"}`}
                      >
                        {a.paused ? "Pausado" : "Ativo"}
                      </span>
                    </td>
                    <td>
                      <div className="agent-actions">
                        <button className="secondary" onClick={() => editAgent(a)}>Renomear</button>
                        <button className="secondary" onClick={() => pauseAgent(a)}>{a.paused ? "Ativar" : "Pausar"}</button>
                        <button className="secondary" onClick={() => deleteAgent(a)}>Excluir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty text="Registre o agente que executará o robô" />
          )}
        </section>
      )}
      {agentEditor && (
        <Modal title={agentEditor === "new" ? "Registrar agente" : "Editar agente"} onClose={() => setAgentEditor(null)}>
          <form className="form-grid" onSubmit={saveAgent}>
            <label>Nome
              <input required minLength={2} maxLength={120} value={agentForm.name}
                onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })} />
            </label>
            <p className="wide">Máquina: {agentEditor === "new" ? "identificada no primeiro sinal do agente" : agentEditor.machine_name || "aguardando o primeiro sinal"}.</p>
            {agentEditor === "new" && <p className="wide">O agente será criado pausado. Ative somente após concluir a troca de máquina.</p>}
            <footer className="wide">
              <button type="button" className="secondary" onClick={() => setAgentEditor(null)}>Cancelar</button>
              <button className="primary">Salvar</button>
            </footer>
          </form>
        </Modal>
      )}
      {agentKey && (
        <Modal title="Chave do agente" onClose={() => setAgentKey(null)}>
          <p>Copie esta chave agora. Ela será exibida apenas uma vez.</p>
          <code className="agent-key">{agentKey}</code>
          <footer className="agent-key-actions">
            <button className="secondary" onClick={() => navigator.clipboard.writeText(agentKey)}>Copiar chave</button>
            <button className="primary" onClick={() => setAgentKey(null)}>Concluído</button>
          </footer>
        </Modal>
      )}
      {open && (
        <Modal title={editing ? "Editar usuário" : "Convidar usuário"} onClose={() => setOpen(false)}>
          <form className="form-grid" onSubmit={save}>
            <label>
              Nome
              <input
                required
                minLength={2}
                maxLength={160}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label>
              E-mail
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
            <label>
              Papel
              <select
                value={form.role}
                disabled={editing?.id === user.id}
                onChange={(e) => setForm({ ...form, role: e.target.value, coordinator_id: null })}
              >
                <option value="colaborador">Colaborador</option>
                <option value="coordenador">Coordenador</option>
                <option value="admin">Administrador</option>
              </select>
            </label>
            <label>
              Equipe
              <select
                value={form.team_id ?? ""}
                required={form.role !== "admin"}
                onChange={(e) =>
                  setForm({ ...form, team_id: e.target.value ? Number(e.target.value) : null, coordinator_id: null })
                }
              >
                <option value="">Sem equipe</option>
                {teams.map((t) => (
                  <option value={t.id} key={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            {form.role === "colaborador" && (
              <label>
                Coordenador
                <select value={form.coordinator_id ?? ""}
                  onChange={(e) => setForm({ ...form, coordinator_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">Sem coordenador</option>
                  {users.filter((u) => u.active && u.role === "coordenador" && u.team_id === form.team_id).map((u) => (
                    <option value={u.id} key={u.id}>{u.name}</option>
                  ))}
                </select>
              </label>
            )}
            {editing && (
              <label className="user-active">
                <input type="checkbox" checked={form.active} disabled={editing.id === user.id}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Usuário ativo
              </label>
            )}
            <footer className="wide">
              <button
                type="button"
                className="secondary"
                onClick={() => setOpen(false)}
              >
                Cancelar
              </button>
              <button className="primary">{editing ? "Salvar alterações" : "Enviar convite"}</button>
            </footer>
          </form>
        </Modal>
      )}
      {pendingInvite && (
        <Modal title="E-mail não enviado" onClose={() => setPendingInvite(null)}>
          <p>O convite foi gerado, mas o e-mail não foi enviado. Verifique a configuração SMTP. Você também pode compartilhar este link diretamente com a pessoa convidada; ele vale por 48 horas e permite definir a senha.</p>
          <p className="alert error">{pendingInvite.warning}</p>
          <code className="agent-key">{pendingInvite.url}</code>
          <footer className="agent-key-actions">
            <button className="secondary" onClick={() => navigator.clipboard.writeText(pendingInvite.url)}>Copiar link</button>
            <button className="primary" onClick={() => setPendingInvite(null)}>Concluído</button>
          </footer>
        </Modal>
      )}
    </>
  );
}

function SettingsPage({ user }: { user: User }) {
  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">SISTEMA</p>
          <h1>Configurações</h1>
          <p>Parâmetros operacionais e regras de segurança.</p>
        </div>
        {user.role === "admin" && (
          <a
            className="primary button-link"
            href="/api/admin/exports/guides.csv"
          >
            Exportar dados das guias
          </a>
        )}
      </div>
      <section className="settings-grid">
        <article className="card">
          <h2>Agenda padrão</h2>
          <dl>
            <div>
              <dt>Competência</dt>
              <dd>Mês anterior</dd>
            </div>
            <div>
              <dt>Recorrência</dt>
              <dd>Diária até encontrar</dd>
            </div>
            <div>
              <dt>Processamento</dt>
              <dd>Sequencial</dd>
            </div>
          </dl>
        </article>
        <article className="card">
          <h2>Retenção e auditoria</h2>
          <dl>
            <div>
              <dt>Arquivo PDF</dt>
              <dd>120 dias</dd>
            </div>
            <div>
              <dt>Hash e extração</dt>
              <dd>Permanente</dd>
            </div>
            <div>
              <dt>Logs de auditoria</dt>
              <dd>Permanente</dd>
            </div>
          </dl>
        </article>
        <article className="card">
          <h2>Segurança</h2>
          <dl>
            <div>
              <dt>Certificados</dt>
              <dd>Windows local</dd>
            </div>
            <div>
              <dt>Chaves privadas</dt>
              <dd>Nunca armazenadas</dd>
            </div>
            <div>
              <dt>Sessões</dt>
              <dd>Cookies seguros</dd>
            </div>
          </dl>
        </article>
      </section>
    </>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const [page, setPage] = useState<Page>("dashboard");
  const [flash, setFlash] = useState<Flash>(null);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  async function load() {
    const [d, c, cert, t, e, g] = await Promise.all([
      api<Dashboard>("/api/dashboard"),
      api<{ items: Company[] }>("/api/companies"),
      api<{ items: Certificate[] }>("/api/certificates"),
      api<{ items: Team[] }>("/api/teams"),
      api<{ items: Execution[] }>("/api/executions"),
      api<{ items: Guide[] }>("/api/guides"),
    ] as const);
    setDashboard(d);
    setCompanies(c.items);
    setCertificates(cert.items);
    setTeams(t.items);
    setExecutions(e.items);
    setGuides(g.items);
    if (user?.role === "admin" || !user)
      api<{ items: Agent[] }>("/api/admin/agents")
        .then((x) => setAgents(x.items))
        .catch(() => {});
  }
  useEffect(() => {
    api<{ user: User }>("/api/auth/me")
      .then((r) => setUser(r.user))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);
  useEffect(() => {
    if (user) load().catch((e) => setFlash({ kind: "error", text: e.message }));
  }, [user]);
  useEffect(() => {
    if (!user) return;
    const stream = new EventSource("/api/events/stream");
    stream.addEventListener("execution", () => load());
    return () => stream.close();
  }, [user]);
  useEffect(() => {
    if (flash) {
      const id = setTimeout(() => setFlash(null), 9000);
      return () => clearTimeout(id);
    }
  }, [flash]);
  const content = useMemo(() => {
    if (!user) return null;
    switch (page) {
      case "dashboard":
        return (
          <DashboardPage
            data={dashboard}
            executions={executions}
            guides={guides}
            companies={companies}
            refresh={load}
          />
        );
      case "companies":
        return (
          <CompaniesPageV2
            items={companies}
            teams={teams}
            certificates={certificates}
            reload={load}
            flash={setFlash}
          />
        );
      case "rules":
        return <RulesPage companies={companies} canManage={user.role === "admin"} reload={load} flash={setFlash} onAssignments={() => setPage("assignments")} />;
      case "assignments":
        return <AssignmentsPage companies={companies} canManage={user.role === "admin"} reload={load} flash={setFlash} onRules={() => setPage("rules")} />;
      case "certificates":
        return (
          <CertificatesPage
            items={certificates}
            agents={agents}
            reload={load}
            flash={setFlash}
          />
        );
      case "executions":
        return (
          <ExecutionsPage
            items={executions}
            companies={companies}
            reload={load}
            flash={setFlash}
            canDelete={user.role === "admin"}
          />
        );
      case "guides":
        return <GuidesPage items={guides} reload={load} flash={setFlash} canDelete={user.role === "admin"} />;
      case "users":
        return (
          <UsersPage
            user={user}
            teams={teams}
            agents={agents}
            reload={load}
            flash={setFlash}
          />
        );
      default:
        return <SettingsPageV2 user={user} flash={setFlash} />;
    }
  }, [
    page,
    user,
    dashboard,
    executions,
    guides,
    companies,
    teams,
    certificates,
    agents,
  ]);
  if (checking)
    return (
      <div className="splash">
        <img src={symbol} alt="Biason" />
        <span>Carregando…</span>
      </div>
    );
  if (!user) {
    const route = location.pathname.slice(1);
    if (
      route === "setup" ||
      route === "accept-invite" ||
      route === "reset-password"
    )
      return <TokenPage kind={route} onLogin={setUser} />;
    return <Login onLogin={setUser} />;
  }
  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    setUser(null);
  }
  return (
    <div className="app">
      <aside>
        <div className="sidebar-brand">
          <img src={logoLight} alt="Biason" />
          <small>BUSCA GUIA INSS</small>
        </div>
        <nav>
          {navigation.map((section) => <div className="nav-section" key={section.title}>
            <small className="nav-section-title">{section.title}</small>
            {section.pages.map((item) => <button
              key={item}
              className={page === item ? "selected" : ""}
              onClick={() => setPage(item)}
            ><span>{navIcon[item]}</span>{labels[item]}</button>)}
          </div>)}
        </nav>
        <div className="sidebar-foot">
          <div
            className={`agent-mini ${dashboard?.agent_online ? "online" : ""}`}
          >
            <i />
            Agente {dashboard?.agent_online ? "online" : "offline"}
          </div>
          <button className="profile" onClick={logout}>
            <span className="avatar">{user.name.charAt(0)}</span>
            <div>
              <strong>{user.name}</strong>
              <small>{user.role}</small>
            </div>
            <b>↪</b>
          </button>
        </div>
      </aside>
      <main className="content">
        {flash && (
          <div className={`toast ${flash.kind}`}>
            {flash.text}
            <button onClick={() => setFlash(null)}>×</button>
          </div>
        )}
        {content}
      </main>
    </div>
  );
}
